import { Component, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil, finalize } from 'rxjs';
import { ChatbotService } from '../../services/chatbot.service';
import { PdfExportService } from '../../services/pdf-export.service';
import { ChatMessage, ExampleQuestion, ExampleCategory } from '../../models/chatbot.model';
import { AuthHelper } from '../../auth/auth-helper';
import { ChartRendererComponent } from './chart-renderer/chart-renderer.component';
import { MarkdownModule } from 'ngx-markdown';
import '@maersk-global/mds-components-core-button';
import '@maersk-global/mds-components-core-icon';
import '@maersk-global/mds-components-core-progress-indicator';

@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MarkdownModule, ChartRendererComponent],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ChatbotComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('chatContainer', { static: false }) chatContainer!: ElementRef;

  chatForm: FormGroup;
  messages: ChatMessage[] = [];
  exampleQuestions: ExampleQuestion[] = [];
  exampleCategories: ExampleCategory[] = [];
  isLoading = false;
  isExamplesLoading = true;
  showSqlQuery: { [messageId: string]: boolean } = {};
  private destroy$ = new Subject<void>();
  private shouldScrollToBottom = false;
  /** Stores the conversation_id returned by the last API response. */
  private currentConversationId: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private chatbotService: ChatbotService,
    private pdfExportService: PdfExportService,
  ) {
    this.chatForm = this.formBuilder.group({
      message: ['', [Validators.required, Validators.minLength(1)]],
    });
  }

  ngOnInit(): void {
    this.loadExampleQuestions();
    //this.addWelcomeMessage();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private addWelcomeMessage(): void {
    const welcomeMessage: ChatMessage = {
      id: this.generateId(),
      text: 'Welcome to AEMS Assistant! I can help you with questions about work orders, equipment, and more. You can start by selecting one of the example questions below or type your own question.',
      isUser: false,
      timestamp: new Date(),
      isLoading: false,
    };
    this.messages.push(welcomeMessage);
    this.shouldScrollToBottom = true;
  }

  private loadExampleQuestions(): void {
    this.chatbotService
      .getExampleCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.exampleCategories = response.examples;
          // Create a flat list with 2 random questions from each category
          this.exampleQuestions = [];
          response.examples.forEach((category) => {
            // Shuffle questions and take first 2
            const shuffledQuestions = this.shuffleArray([...category.questions]);
            const selectedQuestions = shuffledQuestions.slice(0, 2);

            selectedQuestions.forEach((question: string) => {
              this.exampleQuestions.push({
                id: this.generateId(),
                question: question,
                category: category.category,
              });
            });
          });

          // Shuffle the final list to mix categories
          this.exampleQuestions = this.shuffleArray(this.exampleQuestions);
          this.isExamplesLoading = false;
        },
        error: (error) => {
          this.isExamplesLoading = false;
        },
      });
  }

  onSubmit(): void {
    if (this.chatForm.valid && !this.isLoading) {
      const messageText = this.chatForm.get('message')?.value?.trim();
      if (messageText) {
        this.sendMessage(messageText);
        this.chatForm.reset();
      }
    }
  }

  onExampleQuestionClick(question: ExampleQuestion): void {
    if (!this.isLoading) {
      this.sendMessage(question.question);
    }
  }

  onQuestionStringClick(questionText: string): void {
    if (!this.isLoading) {
      this.sendMessage(questionText);
    }
  }

  private sendMessage(messageText: string): void {
    // Add user message
    const userMessage: ChatMessage = {
      id: this.generateId(),
      text: messageText,
      isUser: true,
      timestamp: new Date(),
      isLoading: false,
    };
    this.messages.push(userMessage);

    // Add loading message for bot response
    const loadingMessage: ChatMessage = {
      id: this.generateId(),
      text: '',
      isUser: false,
      timestamp: new Date(),
      isLoading: true,
    };
    this.messages.push(loadingMessage);
    this.shouldScrollToBottom = true;

    this.isLoading = true;

    const followUp = this.detectFollowUp(messageText);
    const conversationIdToSend = followUp ? this.currentConversationId : null;

    // Send message to API
    this.chatbotService
      .sendMessage(messageText, AuthHelper.user?.emailAddress ?? null, conversationIdToSend)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          this.currentConversationId = response.conversation_id;
          // Remove loading message and add actual response
          const loadingIndex = this.messages.findIndex((msg) => msg.id === loadingMessage.id);
          if (loadingIndex > -1) {
            this.messages[loadingIndex] = {
              id: this.generateId(),
              text: response.answer,
              isUser: false,
              timestamp: new Date(),
              isLoading: false,
              conversationId: response.conversation_id,
              turnId: response.turn_id,
              chartData: response.chart_data,
              nlbiMetadata: response.metadata,
              responseTimeMs: response.metadata?.execution_time_ms,
              tokensUsed: response.metadata?.total_tokens,
              isNewContext: !followUp,
            };
          }
          this.shouldScrollToBottom = true;
        },
        error: (_error) => {
          // Replace loading message with error message
          const loadingIndex = this.messages.findIndex((msg) => msg.id === loadingMessage.id);
          if (loadingIndex > -1) {
            this.messages[loadingIndex] = {
              id: this.generateId(),
              text: 'Sorry, I encountered an error while processing your request. Please try again.',
              isUser: false,
              timestamp: new Date(),
              isLoading: false,
            };
          }
          this.shouldScrollToBottom = true;
        },
      });
  }

  private scrollToBottom(): void {
    if (this.chatContainer) {
      const element = this.chatContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    }
  }

  private generateId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSubmit();
    }
  }

  formatMessageContent(content: string): string {
    // Enhance the markdown content for better display
    if (!content) return '';

    // Ensure proper spacing around code blocks
    let formatted = content.replace(/```(\w+)?\n/g, '\n```$1\n');

    // Add language identifier for SQL blocks if missing
    formatted = formatted.replace(/```\n(SELECT|INSERT|UPDATE|DELETE|WITH|CREATE|ALTER|DROP)/gi, '```sql\n$1');

    // Ensure proper line breaks before and after sections
    formatted = formatted.replace(/### (.*?)\n/g, '\n### $1\n\n');
    formatted = formatted.replace(/## (.*?)\n/g, '\n## $1\n\n');

    // Transform table column headers from underscore_case to PascalCase
    formatted = this.transformTableHeaders(formatted);

    return formatted.trim();
  }

  private transformTableHeaders(content: string): string {
    // Find markdown table headers (lines that start and end with |)
    return content.replace(/^\|(.+)\|$/gm, (match) => {
      // Remove the leading and trailing pipes and split by pipes
      const innerContent = match.slice(1, -1); // Remove first and last |
      const headers = innerContent.split('|').map((header: string) => {
        const trimmedHeader = header.trim();
        if (trimmedHeader && this.isUnderscoreCase(trimmedHeader)) {
          const pascalCase = this.toPascalCase(trimmedHeader);
          return ' ' + pascalCase + ' ';
        }
        return header; // Keep original spacing
      });

      const result = '|' + headers.join('|') + '|';
      return result;
    });
  }

  private isUnderscoreCase(str: string): boolean {
    // Check if string contains underscore and looks like a column name
    // More flexible pattern to handle various column name formats
    return /^[a-zA-Z][a-zA-Z0-9_]*_[a-zA-Z0-9_]*$/.test(str);
  }

  private toPascalCase(str: string): string {
    return str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
  }

  toggleSqlQuery(messageId: string): void {
    this.showSqlQuery[messageId] = !this.showSqlQuery[messageId];
  }

  async exportMessageToPdf(message: ChatMessage): Promise<void> {
    try {
      await this.pdfExportService.exportMessageToPdf(message);
    } catch (error) {
      // Error exporting message to PDF
    }
  }

  async exportFormattedMessageToPdf(message: ChatMessage, messageElement?: HTMLElement): Promise<void> {
    try {
      if (messageElement) {
        await this.pdfExportService.exportMessageWithFormattingToPdf(messageElement, message);
      } else {
        await this.pdfExportService.exportMessageToPdf(message);
      }
    } catch (error) {
      // Error exporting formatted message to PDF
    }
  }

  async exportEntireChatToPdf(): Promise<void> {
    try {
      // Filter out loading messages and welcome message for cleaner export
      const exportableMessages = this.messages.filter((msg) => !msg.isLoading && !msg.text.includes('Welcome to AEMS Assistant'));

      if (exportableMessages.length === 0) {
        return;
      }

      await this.pdfExportService.exportChatToPdf(exportableMessages);
    } catch (error) {
      // Error exporting chat to PDF
    }
  }

  trackByMessageId(index: number, message: ChatMessage): string {
    return message.id;
  }

  trackByQuestionId(index: number, question: ExampleQuestion): string {
    return question.id;
  }

  trackByCategoryName(index: number, category: ExampleCategory): string {
    return category.category;
  }

  trackByQuestionText(index: number, question: string): string {
    return question;
  }

  // ─── Rating ──────────────────────────────────────────────────────────────

  readonly stars = [1, 2, 3, 4, 5];

  setHoverRating(message: ChatMessage, star: number): void {
    if (!message.ratingSubmitted) {
      message.pendingRating = star;
    }
  }

  clearHoverRating(message: ChatMessage): void {
    if (!message.ratingSubmitted) {
      message.pendingRating = message.rating ?? 0;
    }
  }

  selectRating(message: ChatMessage, star: number): void {
    if (!message.ratingSubmitted) {
      message.pendingRating = star;
      message.rating = star;
    }
  }

  getStarIcon(message: ChatMessage, star: number): string {
    const active = message.ratingSubmitted ? (message.rating ?? 0) : (message.pendingRating ?? 0);
    return star <= active ? 'star-solid' : 'star';
  }

  submitRating(message: ChatMessage): void {
    if (!message.turnId || !message.rating || message.ratingSubmitted) return;
    this.chatbotService
      .submitFeedback(message.turnId, message.rating, message.ratingComment)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          message.ratingSubmitted = true;
        },
      });
  }

  // ─── New conversation ─────────────────────────────────────────────────────

  clearConversation(): void {
    this.currentConversationId = null;
    this.messages = [];
  }

  // ─── Follow-up detection ─────────────────────────────────────────────────
  /**
   * Returns true when the question almost certainly refers to a prior answer
   * and should be sent with the existing conversation_id (continuing context).
   *
   * Heuristics (any one match = follow-up):
   *  1. Very short question (≤ 4 words) — likely incomplete without prior context.
   *  2. Relative pronouns that imply an already-known referent:
   *     "they / them / their / those / these / it / its"
   *  3. Explicit follow-up starter phrases.
   *  4. Lexical back-references to prior output.
   *
   * If none match, a fresh context window is used so unrelated questions
   * don't bleed prior conversation history into the prompt.
   */
  private detectFollowUp(question: string): boolean {
    if (!this.currentConversationId) return false;

    const q = question.toLowerCase().trim();

    // 1. Very short — almost certainly depends on context
    if (q.split(/\s+/).length <= 4) return true;

    // 2. Relative pronouns used as subject/object (not "this month" etc. — kept
    //    intentionally broad; false positives are cheap, false negatives waste tokens)
    if (/\b(they|them|their|those|these|it\b|its\b)\b/.test(q)) return true;

    // 3. Explicit follow-up starters
    const followUpStartsWith = [
      'what about',
      'how about',
      'and ',
      'also ',
      'why is',
      'why are',
      'why did',
      'why not',
      'which ones',
      'what else',
      'tell me more',
      'show me more',
      'more detail',
      'any other',
      'any more',
      'further breakdown',
      'additionally',
      'similarly',
      'in that case',
      'given that',
      'based on that',
      'for those',
      'among those',
      'what caused',
      'what drove',
      'how so',
      'how come',
      'then what',
      'is that',
      'are those',
      'does that',
      'do those',
      'drill down',
    ];
    if (followUpStartsWith.some((p) => q.startsWith(p))) return true;
    if (/^break (this|it|that|them) down/.test(q)) return true;
    if (/^can you (explain|elaborate|clarify|break|give me more)/.test(q)) return true;
    if (/^regarding (that|those|this)/.test(q)) return true;

    // 4. Back-references to prior output
    if (
      /\b(above|mentioned|previous|that result|those results|the same (shop|work|data|country|code)|that shop|those shops|that country|those countries|that code|that figure|that number)\b/.test(
        q,
      )
    )
      return true;

    return false;
  }
}
